import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Settings2, Info } from "lucide-react";
import { fmtRM, AA_METHODS, AA_HALF_METHODS } from "@/pages/hr/payroll/payrollUtils";

export default function AttendanceAllowanceTab({ employees = [], components = [], leaveTypes = [] }) {
  const { can } = useAuth();
  const canManage = can("payroll:statutory_manage");
  const [rules, setRules] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [edit, setEdit] = useState(null);

  const loadRules = () => api.get("/hr/payroll/attendance-allowance-rules").then((r) => setRules(r.data)).catch(() => {});
  const loadAssignments = () => api.get("/hr/payroll/assignments?component_code=ATT_ALLOW").then((r) => setAssignments(r.data)).catch(() => {});
  useEffect(() => { loadRules(); loadAssignments(); }, []);

  const delRule = async (id) => { if (!window.confirm("Delete this rule?")) return;
    try { await api.delete(`/hr/payroll/attendance-allowance-rules/${id}`); toast.success("Deleted"); loadRules(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const methodLabel = (m) => (AA_METHODS.find((x) => x.value === m) || {}).label || m;

  return (
    <div data-testid="attendance-allowance-tab">
      <div className="rounded-md bg-slate-50 border border-slate-200 text-xs text-slate-600 p-2.5 mb-3 flex items-start gap-2">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>Team membership alone does NOT qualify — only employees with an active <b>Attendance Allowance assignment</b> (set in Payroll Components) receive it, and amounts may differ per employee.
          Per-leave-type treatment below is <b>effective-dated</b> (only <b>approved</b> leave counts): <b>Fixed Per Day</b> deducts an amount per approved day; <b>Full Forfeit</b> (e.g. UL) forfeits the whole allowance and overrides all per-day deductions; deductions can never push the allowance below RM0.
          Half-day treatment that is left <b>unconfigured</b> flags the payroll for review instead of guessing.</span>
      </div>

      <div className="flex justify-between items-center mb-2">
        <h3 className="text-sm font-semibold">Attendance Allowance Rules (per Leave Type, effective-dated)</h3>
        {canManage && <Button size="sm" className="bg-slate-900" data-testid="aa-rule-add-btn" onClick={() => setEdit({ leave_type_code: "", method: "fixed_per_day", amount_per_day: "", half_day_method: "unconfigured", half_day_amount: "", effective_from: "", active: true })}><Plus className="w-4 h-4 mr-1" />Add Rule</Button>}
      </div>
      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto mb-5">
        <table className="excel-table w-full" data-testid="aa-rules-table">
          <thead><tr><th>Leave Type</th><th>Method</th><th>Amount/Day</th><th>Half-Day</th><th>Effective From</th><th>Effective To</th><th>Active</th><th></th></tr></thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id} data-testid={`aa-rule-row-${r.leave_type_code}-${r.id}`}>
                <td className="font-mono">{r.leave_type_code}</td>
                <td><Badge variant="outline">{methodLabel(r.method)}</Badge></td>
                <td>{r.method === "fixed_per_day" ? fmtRM(r.amount_per_day) : "—"}</td>
                <td className="text-xs">{r.method === "fixed_per_day" ? (r.half_day_method === "unconfigured" ? <span className="text-amber-600">Unconfigured (review)</span> : r.half_day_method) : "—"}</td>
                <td className="text-xs">{r.effective_from}</td><td className="text-xs">{r.effective_to || "—"}</td>
                <td className="text-center">{r.active ? "✓" : "—"}</td>
                <td className="text-right">{canManage && <div className="flex gap-1 justify-end">
                  <button onClick={() => setEdit(r)} data-testid={`aa-rule-edit-${r.id}`}><Settings2 className="w-4 h-4 text-slate-500" /></button>
                  <button onClick={() => delRule(r.id)} data-testid={`aa-rule-del-${r.id}`}><Trash2 className="w-4 h-4 text-rose-500" /></button>
                </div>}</td>
              </tr>
            ))}
            {rules.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 py-6">No rules configured. Add AL / MC / UL rules to drive Attendance Allowance deductions.</td></tr>}
          </tbody>
        </table>
      </div>

      <h3 className="text-sm font-semibold mb-2">Assigned Employees (Attendance Allowance base amounts)</h3>
      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="excel-table w-full" data-testid="aa-assignments-table">
          <thead><tr><th>Employee</th><th>Monthly Base</th><th>Effective From</th><th>Effective To</th><th>Active</th></tr></thead>
          <tbody>
            {assignments.map((a) => (
              <tr key={a.id} data-testid={`aa-assign-row-${a.employee_id}`}>
                <td className="font-mono text-xs">{a.employee_id}</td><td>{fmtRM(a.amount)}</td>
                <td className="text-xs">{a.effective_from || "—"}</td><td className="text-xs">{a.effective_to || "—"}</td>
                <td className="text-center">{a.active ? "✓" : "—"}</td>
              </tr>
            ))}
            {assignments.length === 0 && <tr><td colSpan={5} className="text-center text-slate-400 py-6">No employees assigned. Assign the "Attendance Allowance" component with a base amount in Payroll Components → Employee Assignments.</td></tr>}
          </tbody>
        </table>
      </div>

      {edit && <RuleDialog rule={edit} leaveTypes={leaveTypes} onClose={() => setEdit(null)} onSaved={loadRules} />}
    </div>
  );
}

function RuleDialog({ rule, leaveTypes, onClose, onSaved }) {
  const [r, setR] = useState(rule);
  const [busy, setBusy] = useState(false);
  const isNew = !rule.id;
  const set = (k, v) => setR({ ...r, [k]: v });
  const save = async () => {
    if (!r.leave_type_code || !r.effective_from) return toast.error("Leave type and effective date required");
    if (r.method === "fixed_per_day" && (r.amount_per_day === "" || r.amount_per_day === null)) return toast.error("Amount per day required for Fixed Per Day");
    setBusy(true);
    try {
      const body = { ...r,
        amount_per_day: r.amount_per_day === "" ? null : Number(r.amount_per_day),
        half_day_amount: r.half_day_amount === "" ? null : Number(r.half_day_amount) };
      if (isNew) await api.post("/hr/payroll/attendance-allowance-rules", body);
      else await api.put(`/hr/payroll/attendance-allowance-rules/${r.id}`, body);
      toast.success("Saved"); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const lt = leaveTypes.find((t) => t.code === r.leave_type_code);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="aa-rule-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Add" : "Edit"} Attendance Allowance Rule</DialogTitle><DialogDescription>Effective-dated so historical payroll is unchanged when amounts change later.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Leave Type</Label>
            {isNew ? (
              <Select value={r.leave_type_code} onValueChange={(v) => { const t = leaveTypes.find((x) => x.code === v); set("leave_type_code", v); setR((p) => ({ ...p, leave_type_code: v, leave_type_id: t?.id })); }}>
                <SelectTrigger data-testid="aa-rule-leavetype"><SelectValue placeholder="Select leave type" /></SelectTrigger>
                <SelectContent>{leaveTypes.map((t) => <SelectItem key={t.id} value={t.code}>{t.code} · {t.name}</SelectItem>)}</SelectContent>
              </Select>
            ) : <Input value={r.leave_type_code} disabled />}
          </div>
          <div><Label className="text-xs">Method</Label>
            <Select value={r.method} onValueChange={(v) => set("method", v)}>
              <SelectTrigger data-testid="aa-rule-method"><SelectValue /></SelectTrigger>
              <SelectContent>{AA_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
            </Select></div>
          {r.method === "fixed_per_day" && <>
            <div><Label className="text-xs">Amount Per Day (RM)</Label><Input type="number" step="0.01" value={r.amount_per_day ?? ""} onChange={(e) => set("amount_per_day", e.target.value)} data-testid="aa-rule-amount" placeholder="e.g. 50" /></div>
            <div><Label className="text-xs">Half-Day Treatment</Label>
              <Select value={r.half_day_method || "unconfigured"} onValueChange={(v) => set("half_day_method", v)}>
                <SelectTrigger data-testid="aa-rule-halfmethod"><SelectValue /></SelectTrigger>
                <SelectContent>{AA_HALF_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
              </Select></div>
            {r.half_day_method === "fixed" && <div><Label className="text-xs">Half-Day Fixed Amount (RM)</Label><Input type="number" step="0.01" value={r.half_day_amount ?? ""} onChange={(e) => set("half_day_amount", e.target.value)} data-testid="aa-rule-halfamount" /></div>}
          </>}
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Effective From</Label><Input type="date" value={r.effective_from} onChange={(e) => set("effective_from", e.target.value)} data-testid="aa-rule-eff-from" /></div>
            <div><Label className="text-xs">Effective To</Label><Input type="date" value={r.effective_to || ""} onChange={(e) => set("effective_to", e.target.value)} /></div>
          </div>
          <div className="flex items-center gap-2"><Switch checked={!!r.active} onCheckedChange={(v) => set("active", v)} /><Label className="text-xs">Active</Label></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button className="bg-slate-900" disabled={busy} onClick={save} data-testid="aa-rule-save-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
